"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Like `useRevealOnce`, but hands back a boolean instead of adding a class:
 * for entrances that JavaScript has to drive (a counter ticking up, say)
 * rather than a CSS keyframe. Fires once, then stops observing.
 */
export function useInViewOnce<T extends HTMLElement>(threshold = 0.4) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      // Next frame rather than inline: setting state straight from an effect
      // body is a cascading render, and this is the no-observer fallback, so
      // one frame late costs nothing.
      const raf = requestAnimationFrame(() => setInView(true));
      return () => cancelAnimationFrame(raf);
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setInView(true);
        io.disconnect();
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, inView };
}
